import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Filter, AlertTriangle, GitBranch, Eye, EyeOff } from 'lucide-react';
import { RootState, AppDispatch } from '../../store';
import {
  fetchDependencies,
  fetchAtRiskDependencies,
  clearError
} from '../../store/slices/dependenciesSlice';
import {
  GlassCard,
  GlassButton,
  GlassModal,
  GlassPageHeader,
  GlassAlert,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';
import { DependencyList } from './components/DependencyList';
import { DependencyForm } from './components/DependencyForm';
import { DependencyFilters } from './components/DependencyFilters';
import { DependencyVisualization } from './components/DependencyVisualization';
import { DependencyAlerts } from './components/DependencyAlerts';


const DependenciesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    dependencies, 
    isLoading, 
    error, 
    filters, 
    summary 
  } = useSelector((state: RootState) => state.dependencies);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showVisualization, setShowVisualization] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'at-risk'>('all');

  useEffect(() => {
    if (viewMode === 'all') {
      dispatch(fetchDependencies(filters));
    } else {
      dispatch(fetchAtRiskDependencies());
    }
  }, [dispatch, filters, viewMode]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateDependency = () => {
    setShowCreateModal(true);
  };

  const handleViewModeChange = (mode: 'all' | 'at-risk') => {
    setViewMode(mode);
  };



  return (
    <div className="min-h-screen glass-page-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <SectionReveal>
          <GlassPageHeader
            title="Dependencies"
            subtitle="Manage cross-entity dependencies and track blocking relationships"
            icon={<GitBranch className="w-6 h-6" />}
            iconGradient="blue-violet"
            className="mb-6"
            actions={
              <div className="flex items-center space-x-3">
                <GlassButton
                  variant="secondary"
                  onClick={() => setShowVisualization(!showVisualization)}
                  leftIcon={showVisualization ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                >
                  {showVisualization ? 'Hide' : 'Show'} Graph
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  onClick={() => setShowFilters(!showFilters)}
                  leftIcon={<Filter className="w-4 h-4" />}
                >
                  Filters
                </GlassButton>
                <GlassButton
                  variant="primary"
                  onClick={handleCreateDependency}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Add Dependency
                </GlassButton>
              </div>
            }
          />
        </SectionReveal>

        {/* Error Alert */}
        {error && (
          <SectionReveal delay={0.1}>
            <GlassAlert variant="error" message={error} />
          </SectionReveal>
        )}

        {/* View Mode Toggle */}
        <SectionReveal delay={0.15}>
          <div className="flex items-center space-x-4">
            <div className="flex bg-surface-glass rounded-lg p-1">
              <button
                onClick={() => handleViewModeChange('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'all'
                    ? 'bg-accent-blue text-white'
                    : 'text-muted-foreground/60 hover:text-foreground'
                }`}
              >
                All Dependencies
              </button>
              <button
                onClick={() => handleViewModeChange('at-risk')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'at-risk'
                    ? 'bg-accent-rose text-white'
                    : 'text-muted-foreground/60 hover:text-foreground'
                }`}
              >
                At Risk
              </button>
            </div>

            {summary && (
              <div className="flex items-center space-x-4 text-sm text-muted-foreground/60">
                <span>Total: {summary.total}</span>
                <span>At Risk: {summary.atRisk ?? (summary.overdue ?? 0) + (summary.dueSoon ?? 0)}</span>
                <span>Critical: {summary.bySeverity?.CRITICAL ?? summary.critical ?? 0}</span>
                <span>High: {summary.bySeverity?.HIGH ?? summary.high ?? 0}</span>
              </div>
            )}
          </div>
        </SectionReveal>

        {/* Summary Cards */}
        {summary && (
          <SectionReveal delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <GlassCard padding="md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground/60">Total Dependencies</p>
                    <p className="text-2xl font-bold text-foreground">{summary.total}</p>
                  </div>
                  <GitBranch className="w-8 h-8 text-accent-blue" />
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground/60">At Risk</p>
                    <p className="text-2xl font-bold text-accent-rose">{summary.atRisk ?? (summary.overdue ?? 0) + (summary.dueSoon ?? 0)}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-accent-rose" />
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground/60">Blocking</p>
                    <p className="text-2xl font-bold text-accent-amber">{summary.byType?.BLOCKING ?? summary.blocking ?? 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-accent-amber" />
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground/60">Open</p>
                    <p className="text-2xl font-bold text-accent-amber">{summary.byStatus?.OPEN ?? 0}</p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-accent-amber/20 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-accent-amber" />
                  </div>
                </div>
              </GlassCard>
            </div>
          </SectionReveal>
        )}

        {/* Filters */}
        {showFilters && (
          <SectionReveal delay={0.25}>
            <GlassCard padding="md">
              <DependencyFilters />
            </GlassCard>
          </SectionReveal>
        )}

        {/* Dependency Alerts */}
        <SectionReveal delay={0.3}>
          <DependencyAlerts />
        </SectionReveal>

        {/* Visualization */}
        {showVisualization && (
          <SectionReveal delay={0.35}>
            <GlassCard padding="md">
              <h3 className="text-lg font-semibold text-foreground mb-4">Dependency Graph</h3>
              <DependencyVisualization dependencies={dependencies} />
            </GlassCard>
          </SectionReveal>
        )}

        {/* Dependencies List */}
        <SectionReveal delay={0.4}>
          <GlassCard padding="lg">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <GlassLoadingSpinner size="lg" variant="gradient" label="Loading dependencies..." />
              </div>
            ) : (
              <DependencyList
                dependencies={dependencies}
                viewMode={viewMode}
              />
            )}
          </GlassCard>
        </SectionReveal>

        {/* Create Dependency Modal */}
        <GlassModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create Dependency"
          subtitle="Define a new cross-entity dependency"
          icon={<Plus className="w-5 h-5" />}
          iconGradient="emerald"
          size="lg"
        >
          <DependencyForm
            onSuccess={() => {
              setShowCreateModal(false);
              if (viewMode === 'all') {
                dispatch(fetchDependencies(filters));
              } else {
                dispatch(fetchAtRiskDependencies());
              }
            }}
            onCancel={() => setShowCreateModal(false)}
          />
        </GlassModal>
      </div>
    </div>
  );
};

export default DependenciesPage;